import React, { useState } from 'react';
import { POPULAR_SERVICES } from '../../data/servicesData';
import ServiceCard from '../../components/ServiceCard';
import TopBar from '../../components/layout/TopBar';
import Footer from '../../components/layout/Footer';
import { buildConfiguredBookingData, getServiceDisplayPrice } from '../../config/servicePricingConfig';
import QuickBookingModal from '../../components/QuickBookingModal';

const MostBookedServicesPage = ({ onNavigate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('popularity');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showQuickBookingModal, setShowQuickBookingModal] = useState(false);
  const [preBookingData, setPreBookingData] = useState(null);

  // Get unique categories
  const categories = ['all', ...new Set(POPULAR_SERVICES.map(service => service.category))];

  // Filter and sort services
  const filteredServices = POPULAR_SERVICES.filter(service => {
    const matchesSearch = service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || service.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const sortedServices = filteredServices
    .map((service) => ({
      ...service,
      price: getServiceDisplayPrice(service),
      originalPrice: getServiceDisplayPrice(service)
    }))
    .sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      case 'rating':
        return b.rating - a.rating;
      case 'popularity':
      default:
        return b.reviewCount - a.reviewCount;
    }
    });

  const handleBookNow = (service) => {
    const bookingData = buildConfiguredBookingData(service, 'most-booked-services');
    setPreBookingData(bookingData);
    setShowQuickBookingModal(true);
  };

  const handlePreBookingContinue = (bookingData) => {
    if (!onNavigate) return;
    onNavigate('booking', { bookingData });
  };

  const handleLearnMore = (service) => {
    if (onNavigate) {
      onNavigate('service-details', {
        service: {
          ...service,
          servicePage: 'most-booked-services'
        }
      });
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const formatDisplayName = (name) => {
    if (name === 'all') return 'All Categories';
    return name.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* TopBar */}
      <TopBar 
        onUserClick={() => onNavigate && onNavigate('login')}
        onSearch={handleSearch}
        onSignupUser={() => onNavigate && onNavigate('signup')}
        onSignupPandit={() => onNavigate && onNavigate('signup')}
        onLogoClick={() => onNavigate && onNavigate('home')}
      />

      {/* Hero Section */}
      <section className="py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="mb-6">
            <ol className="flex items-center space-x-2 text-sm text-gray-600">
              <li>
                <button
                  onClick={() => onNavigate && onNavigate('home')}
                  className="hover:text-orange-600 transition-colors"
                >
                  Home
                </button>
              </li>
              <li className="flex items-center">
                <svg className="w-4 h-4 mx-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <span className="text-gray-900 font-medium">Most Booked Services</span>
              </li>
            </ol>
          </nav>

          {/* Hero Content */}
          <div className="text-center bg-white rounded-2xl p-8 shadow-lg border border-orange-100">
            <div className="text-6xl mb-4">🔥</div>
            <h1 className="text-4xl lg:text-5xl font-bold text-orange-800 mb-4">
              Most Booked Services
            </h1>
            <p className="text-lg text-gray-600 mb-6 max-w-3xl mx-auto">
              Discover our most popular spiritual services trusted by thousands of customers
            </p>

            {/* Stats */}
            <div className="flex justify-center items-center space-x-8 mb-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">{POPULAR_SERVICES.length}+</div>
                <div className="text-sm text-gray-600">Services</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">{POPULAR_SERVICES.reduce((sum, service) => sum + (service.reviewCount || 0), 0)}+</div>
                <div className="text-sm text-gray-600">Total Bookings</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">{(POPULAR_SERVICES.reduce((sum, service) => sum + (service.rating || 0), 0) / POPULAR_SERVICES.length).toFixed(1)}★</div>
                <div className="text-sm text-gray-600">Average Rating</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content with Sidebar Layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="grid lg:grid-cols-4 gap-6 lg:gap-8">
          {/* Sidebar Filters */}
          <div className="lg:col-span-1 order-2 lg:order-1">
            <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 sticky top-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Filter Services</h3>
              
              {/* Search */}
              <div className="mb-6">
                <input
                  type="text"
                  placeholder="Search popular services..."
                  className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm sm:text-base"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Categories */}
              <div className="space-y-2 mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Categories</h4>
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => setFilterCategory(category)}
                    className={`w-full text-left px-3 sm:px-4 py-2 sm:py-3 rounded-lg transition-colors text-sm sm:text-base ${
                      filterCategory === category
                        ? 'bg-orange-50 text-orange-700 border border-orange-200'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <span>{formatDisplayName(category)}</span>
                      <span className={`text-xs sm:text-sm ${
                        filterCategory === category ? 'text-orange-600' : 'text-gray-500'
                      }`}>
                        {category === 'all' ? POPULAR_SERVICES.length : 
                         POPULAR_SERVICES.filter(s => s.category === category).length}
                      </span>
                    </div>
                  </button>
                ))}
              </div>

              {/* Sort Options */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Sort By</h4>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                >
                  <option value="popularity">Most Popular</option>
                  <option value="rating">Highest Rated</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                </select>
              </div>

              {/* Results Summary */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  <div>Showing {sortedServices.length} of {POPULAR_SERVICES.length} services</div>
                  {(searchTerm || filterCategory !== 'all') && (
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setFilterCategory('all');
                        setSortBy('popularity');
                      }}
                      className="text-orange-600 hover:text-orange-700 font-medium mt-2 text-xs"
                    >
                      Clear all filters
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Services Grid */}
          <div className="lg:col-span-3 order-1 lg:order-2">
            {sortedServices.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
                {sortedServices.map((service) => (
                  <ServiceCard
                    key={service.id}
                    service={service}
                    onBookNow={handleBookNow}
                    onLearnMore={handleLearnMore}
                    enhanced={true}
                    className="h-full"
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="text-6xl mb-4">🔍</div>
                <h3 className="text-xl font-medium text-gray-900 mb-2">No services found</h3>
                <p className="text-gray-600 mb-4">Try adjusting your search or filter criteria</p>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setFilterCategory('all');
                    setSortBy('popularity');
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-all duration-200 font-medium"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer onNavigate={onNavigate} />

      <QuickBookingModal
        isOpen={showQuickBookingModal}
        bookingData={preBookingData}
        onClose={() => {
          setShowQuickBookingModal(false);
          setPreBookingData(null);
        }}
        onContinue={handlePreBookingContinue}
      />
    </div>
  );
};

export default MostBookedServicesPage;

